import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import ImageUploader from "../ImageUploader";
import DateField from "./check-form/DateField";
import ProjectField from "./check-form/ProjectField";
import PersonField from "./check-form/PersonField";
import DescriptionField from "./check-form/DescriptionField";
import CurrencyDropdown from '@/components/common/CurrencyDropdown';
import { getCurrencyAmountLabel } from "@/utils/currencyUtils";
import { useUserPreferences } from '@/contexts/UserPreferencesContext';
import { useTranslation } from "react-i18next";

interface CheckFormProps {
  isGivenCheck: boolean;
  date: Date;
  dueDate: Date;
  projectId: string;
  personId: string;
  description: string;
  amount: string;
  imageFile: File | null;
  imagePreview: string | null;
  onDateChange: (date: Date) => void;
  onDueDateChange: (date: Date) => void;
  onProjectChange: (projectId: string) => void;
  onPersonChange: (personId: string) => void;
  onDescriptionChange: (description: string) => void;
  onAmountChange: (amount: string) => void;
  onImageChange: (file: File) => void;
  onSubmit: (e: React.FormEvent) => void;
  currency?: string;
  onCurrencyChange?: (currency: string) => void;
}

const CheckForm: React.FC<CheckFormProps> = ({
  isGivenCheck,
  date,
  dueDate,
  projectId,
  personId,
  description,
  amount,
  imageFile,
  imagePreview,
  onDateChange,
  onDueDateChange,
  onProjectChange,
  onPersonChange,
  onDescriptionChange,
  onAmountChange,
  onImageChange,
  onSubmit,
  currency,
  onCurrencyChange = () => {},
}) => {
  const { preferences } = useUserPreferences();
  const { t } = useTranslation();
  const formTitle = isGivenCheck ? t("entries.types.given_check") : t("entries.types.received_check");
  const [selectedCurrency, setSelectedCurrency] = useState(currency || preferences?.base_currency || 'TRY');

  // Set the initial currency from user preferences if not already set
  useEffect(() => {
    if (!currency && preferences?.base_currency && selectedCurrency !== preferences.base_currency) {
      setSelectedCurrency(preferences.base_currency);
      onCurrencyChange(preferences.base_currency);
    }
  }, [preferences, currency, onCurrencyChange, selectedCurrency]);

  const handleCurrencyChange = (value: string) => {
    setSelectedCurrency(value);
    onCurrencyChange(value);
  };

  const isFormValid = () => {
    return !!date && !!dueDate && !!projectId && !!personId && 
      description.trim() !== '' && amount.trim() !== '';
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4 pb-6 pt-4 px-4">
      {/* Official record switch row */}
      <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-xs">
        <div>
          <p className="text-[13px] font-semibold text-gray-900">Resmi Kayıt</p>
          <p className="text-[11px] text-gray-500">Fatura / resmi belge mevcut</p>
        </div>
        <label className="relative inline-flex cursor-pointer items-center">
          <input type="checkbox" className="sr-only peer" />
          <div className="h-5 w-9 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all peer-checked:bg-brand-600 peer-checked:after:translate-x-4" />
        </label>
      </div>

      <DateField
        label={t("entries.date")}
        date={date}
        onDateChange={onDateChange}
      />

      <DateField
        label={t("person_detail.due_date")}
        date={dueDate}
        onDateChange={onDueDateChange}
        placeholder="gg/aa/yyyy"
      />

      <ProjectField
        projectId={projectId}
        onProjectChange={onProjectChange}
      />

      <PersonField
        personId={personId}
        onPersonChange={onPersonChange}
        isGivenCheck={isGivenCheck}
      />

      {/* Amount field with currency prefix */}
      <div className="space-y-1.5">
        <label htmlFor="amount" className="block text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-600">
          {getCurrencyAmountLabel(selectedCurrency)}
        </label>
        <div className="flex items-center rounded-md border border-gray-200 bg-white focus-within:border-brand-600 focus-within:ring-1 focus-within:ring-brand-300 overflow-hidden">
          <span className="flex-shrink-0 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-500 border-r border-gray-200 bg-gray-50 select-none">
            {selectedCurrency}
          </span>
          <input
            id="amount"
            type="number"
            inputMode="decimal"
            pattern="[0-9]*\.?[0-9]*"
            placeholder="0.00"
            value={amount}
            onChange={(e) => onAmountChange(e.target.value)}
            className="flex-1 px-3 py-2 text-[13px] font-mono tabular-nums text-gray-900 bg-transparent outline-none placeholder:text-gray-400"
          />
          <div className="pr-2">
            <CurrencyDropdown
              value={selectedCurrency}
              onValueChange={handleCurrencyChange}
            />
          </div>
        </div>
      </div>

      <DescriptionField
        description={description}
        onDescriptionChange={onDescriptionChange}
      />

      <ImageUploader
        imageFile={imageFile}
        imagePreview={imagePreview}
        onImageChange={onImageChange}
      />

      {/* Footer: Vazgeç + Kaydet */}
      <div className="flex gap-3 pt-2">
        <Button
          type="button"
          variant="outline"
          className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-50"
          onClick={() => {
            const form = document.querySelector('form');
            if (form) form.dispatchEvent(new Event('reset'));
          }}
        >
          Vazgeç
        </Button>
        <Button
          type="submit"
          className="flex-1 bg-brand-600 hover:bg-brand-700 active:bg-brand-800 text-white"
          disabled={!isFormValid()}
        >
          {t("common.save")}
        </Button>
      </div>
    </form>
  );
};

export default CheckForm;
